// Коды ошибок и формат ошибки — tech/03 §1.1.
// Общая таблица §1.1 + коды из строк эндпоинтов прототипа, которых в §1.1 нет (решение каркаса Р-05):
// их HTTP-статус взят из строки эндпоинта.
import { z } from 'zod';
import { strict } from './common.js';

const byStatus = {
  400: ['VALIDATION_FAILED'],
  401: [
    'AUTH_REQUIRED', 'TOKEN_EXPIRED', 'INIT_DATA_INVALID', 'INIT_DATA_EXPIRED', 'SIGNATURE_INVALID', 'SESSION_ELSEWHERE',
    'REFRESH_REUSED', // 03 §2 /auth/refresh
    'ADMIN_LOGIN_FAILED', 'TOTP_REQUIRED', // 08 §7 вход в админку: неверные данные; почта и пароль без кода TOTP
  ],
  403: [
    'BANNED', 'FORBIDDEN_ROLE', 'FEATURE_DISABLED', 'TALONS_DEBT', 'PURCHASE_BLOCKED', 'TG_LINK_REQUIRED', 'CHAT_MUTED',
    'COOP_CLOSED', 'COOP_FROZEN', 'CONSENT_REQUIRED', 'SPEND_LIMIT_REACHED',
    'FORBIDDEN_ORIGIN', // 03 §1 «CORS»: чужой Origin на изменяющем запросе → 403 (кода в §1.1 нет)
    'LIMIT_EXCEEDED', 'CSRF_INVALID', 'OWN_SANCTION', // 03 §22, 10 §1: лимит роли, токен CSRF админки (08 §7), апелляция на свою санкцию
  ],
  404: ['NOT_FOUND'],
  409: [
    'CONFLICT', 'IDEMPOTENCY_MISMATCH', 'STALE_TURN', 'BATTLE_ALREADY_ACTIVE', 'ALREADY_IN_COOP', 'COOP_FULL',
    'REQUESTS_LIMIT', 'SOLD_OUT', 'ALREADY_PURCHASED', 'BOSS_DEFEATED', 'RAID_PHASE', 'STALE_ITEM', 'SHOWCASE_EXPIRED',
    'PRICE_CHANGED', 'REMOVAL_PENDING', 'ELECTION_ACTIVE', 'ALREADY_VOTED', 'ALREADY_SUBMITTED', 'COMBO_TAKEN', 'WISH_STATE',
    'IN_BATTLE', // 03 §4 equip/unequip, §3 stats/reset
    'BATTLE_OVER', // 03 §6 turns, retreat
    'STEP_OUT_OF_ORDER', // 03 §3 /tutorial/step
    'ALREADY_PAID', // 03 §12 cancel
    'NOT_UPGRADING', 'CANCEL_WINDOW_PASSED', // 03 §5 гараж: speedup/cancel без стройки, отмена позже 5 мин
    'TRIP_RUNNING', 'NOT_RUNNING', 'NOT_FINISHED', 'ALREADY_CLAIMED', // 03 §5 машина и выезды
    'NAME_TAKEN', 'CHAIRMAN_MUST_TRANSFER', 'NOT_SCHEDULED', 'DISBAND_SCHEDULED', 'INSTANCE_ACTIVE', // 03 §7 кооперативы
    'ALREADY_RESPECTED_TODAY', // 03 §3 respect
    'ALREADY_REFUNDED', 'REFUND_IN_PROGRESS', 'ALREADY_APPEALED', 'SAME_SIGNER', 'NOT_VALIDATED', 'NOT_APPROVED', // 03 §18, §22
    'APPROVAL_STATE', 'CONFIG_STATE', // 03 §22 (Р-А01): утверждение уже решено; версия конфига не в том статусе
    'ALREADY_REPORTED', // 03 §8 жалоба на сообщение
    'HAS_PENDING_PAYMENTS', 'DELETE_ALREADY_SCHEDULED', // 03 §3 удаление аккаунта (E02-T05)
  ],
  410: ['GONE'],
  422: [
    'NOT_ENOUGH_ENERGY', 'NOT_ENOUGH_NUTS', 'NOT_ENOUGH_DIESEL', 'NOT_ENOUGH_TALONS', 'NOT_ENOUGH_KEYS',
    'NOT_ENOUGH_TICKETS', 'NOT_ENOUGH_SCRAP', 'NOT_ENOUGH_MASTER_XP', 'NOT_ENOUGH_S8_TOKENS', 'NOT_ENOUGH_MARKS',
    'NOT_ENOUGH_EVENT_TOKENS',
    'LEVEL_TOO_LOW', 'BOSS_LOCKED', 'COOLDOWN', 'DAILY_LIMIT', 'WEEKLY_LIMIT', 'LIMIT_REACHED', 'RAID_CAP',
    'CHAT_REJECTED', 'REQUIREMENT_NOT_MET', 'TRANSFER_NOT_ALLOWED', 'MASTERY_NOT_AVAILABLE', 'ALREADY_FULL',
    'LIMIT_INVALID', 'TEXT_REJECTED',
    'NOT_ENOUGH_POINTS', // 03 §3 /me/stats
    'VERSION_OUTDATED', // 03 §3 /me/consents
    'ITEM_LOCKED', 'ITEM_EQUIPPED', 'MAX_LEVEL', // 03 §4
    'MODE_NOT_ALLOWED', 'NOT_IN_COOP', // 03 §6 POST /battles
    'CAR_NOT_READY', // 03 §5 POST /car/trips: узлы ниже требований маршрута
    'NAME_INVALID', 'REJOIN_COOLDOWN', 'NO_SUMMONS', 'BOSS_NOT_ELIGIBLE', 'NO_PLANS', 'COOP_LEVEL_TOO_LOW', 'BUILDING_MAX',
    'STORAGE_FULL', 'TENURE_TOO_SHORT', // 03 §7 кооперативы
    'SELF', 'FRIENDS_LIMIT', // 03 §3 respect, §15 друзья, жалобы
    'REASON_REQUIRED', 'CONFIG_INVALID', 'ACTIVATE_TOO_SOON', 'REVIEW_REQUIRED', // 03 §22, 10 §0, §2.2 (Р-А01)
    'BODY_INVALID', 'NOT_COMPLETED', 'URL_INVALID', // 03 §8 чат, §9 задания, §7 группа Telegram
  ],
  413: ['PAYLOAD_TOO_LARGE'], // 03 §17 /analytics/batch: тело больше 256 КБ
  426: ['CLIENT_TOO_OLD'],
  429: ['RATE_LIMITED'],
  500: ['INTERNAL'],
  503: ['MAINTENANCE', 'UNAVAILABLE', 'INVOICE_UNAVAILABLE'],
} as const;

type ByStatus = typeof byStatus;
export type ErrorCode = ByStatus[keyof ByStatus][number];
export type HttpErrorStatus = keyof ByStatus;

export const ERROR_HTTP_STATUS: Readonly<Record<ErrorCode, HttpErrorStatus>> = Object.fromEntries(
  Object.entries(byStatus).flatMap(([status, codes]) => codes.map((c) => [c, Number(status)])),
) as Record<ErrorCode, HttpErrorStatus>;

export const ERROR_CODES = Object.keys(ERROR_HTTP_STATUS) as [ErrorCode, ...ErrorCode[]];
export const ErrorCodeSchema = z.enum(ERROR_CODES);

/** Коды, которые может вернуть любой эндпоинт (03 §1.1, «Как читать столбцы», А1-061). */
export const COMMON_ERRORS = [
  'VALIDATION_FAILED', 'AUTH_REQUIRED', 'TOKEN_EXPIRED', 'SIGNATURE_INVALID', 'SESSION_ELSEWHERE', 'BANNED',
  'CLIENT_TOO_OLD', 'RATE_LIMITED', 'INTERNAL', 'MAINTENANCE', 'UNAVAILABLE',
] as const satisfies readonly ErrorCode[];

/** Повторять ли запрос (03 §1.1 столбец «Повторять?»). Для 409 решает `details`, по умолчанию — нет. */
export function isRetryable(code: ErrorCode): boolean {
  return code === 'RATE_LIMITED' || code === 'INTERNAL' || ERROR_HTTP_STATUS[code] === 503;
}

/** Тело ошибки (03 §1.1). `message` — для разработчика; клиент показывает свой текст по `code`. */
export const ErrorBody = strict({
  error: strict({
    code: ErrorCodeSchema,
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
    requestId: z.string(),
    retryable: z.boolean(),
  }),
});
export type ErrorBody = z.infer<typeof ErrorBody>;

/** `details` у 403 BANNED (E02-T07, Р-95): срок (null — бессрочно), категория санкции и публичный ID для «Оспорить» в боте. */
export const BannedDetails = z.object({ until: z.string().nullable(), category: z.string(), publicId: z.string() });
export type BannedDetails = z.infer<typeof BannedDetails>;
